import type { FC } from 'react';
import { useEffect, useMemo } from 'react';
import type {
  PermissionGroup,
  PermissionGroupAssignedUserDetailsParams,
  PermissionGroupDetailsResponse,
} from 'src/apis/authorization-service/types';
import { useDeletePermissionGroup } from 'src/apis/authorization-service/use-delete-permission-group';
import { usePermissionGroupAssignedUsers } from 'src/apis/authorization-service/use-permission-group-assigned-users';
import { Stack } from 'src/components/layout/stack';
import { Modal } from 'src/components/overlay/modal';
import type { SavedReferrer } from 'src/hooks/use-referrer';
import { useToast } from 'src/hooks/use-toasts';
import { UserTableWrapper } from 'src/pages/settings/permission-groups/overview/user-list';

type Props = {
  close: () => void;
  onSuccess: () => void;
  deleteUrl: string;
  permissionGroup: PermissionGroup | PermissionGroupDetailsResponse;
  savedReferrer: SavedReferrer;
};

export const DeletePermissionGroupModal: FC<Props> = ({
  close,
  onSuccess,
  deleteUrl,
  permissionGroup,
  savedReferrer,
}) => {
  const notify = useToast();
  const deletePermissionGroup = useDeletePermissionGroup();

  const assignedUsersParams = useMemo(
    (): PermissionGroupAssignedUserDetailsParams => ({
      limit: 500,
      offset: 0,
    }),
    []
  );
  const assignedUsers = usePermissionGroupAssignedUsers({
    assignedUsersLink: permissionGroup._links.assignedUsers,
    params: assignedUsersParams,
  });

  useEffect(() => {
    if (!deletePermissionGroup.response) return;

    notify({
      type: 'success',
      children: 'Permission group successfully deleted.',
    });
    onSuccess();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deletePermissionGroup.response]);

  const usersCount = permissionGroup.assignedUserCount;

  return (
    <Modal
      data-testid="delete-permission-group-modal"
      title="Delete Permission Group"
      onDismiss={close}
      footer={{
        confirmButton: {
          label: deletePermissionGroup.pending
            ? 'Deleting...'
            : 'Delete Permission Group',
          onClick: () => deletePermissionGroup.execute(deleteUrl),
          dataTestId: 'delete-permission-group-submit',
        },
      }}
    >
      <Stack gap={1.5}>
        <p>
          Please, confirm that you want to delete the permission group "
          {permissionGroup.name}".
        </p>

        {usersCount > 0 ? (
          <>
            <p data-testid="delete-permission-group-modal-user-info">
              The following {usersCount > 1 ? 'users' : 'user'} will be
              unassigned from this group automatically:
            </p>
            <UserTableWrapper
              savedReferrer={savedReferrer}
              assignedUsers={assignedUsers}
            />
          </>
        ) : (
          <p data-testid="delete-permission-group-modal-user-info">
            Currently no users are assigned to it.
          </p>
        )}
      </Stack>
    </Modal>
  );
};
